import { Diagnostic, StatusData } from "@fairfleet/geotab";
import { LiveDataRow } from "./LiveDataRow";

/**
 * The unit a diagnostic is shown in for this user, as their measurement profile defines it.
 *
 * Geotab reports every reading in a base unit and leaves the conversion to the profile, so the
 * factor and offset have to travel with the unit name — the name alone would label a value in
 * metres as kilometres.
 */
export interface DisplayUnit {
  name: string;
  factorFromBase?: number;
  offsetFromBase?: number;
}

/** Everything that has to be looked up per reading, indexed by diagnostic id. */
export interface LiveDataLookups {
  /** Diagnostics as far as they are known yet; they arrive from a separate query. */
  diagnostics: Record<string, Diagnostic>;
  units: Record<string, DisplayUnit>;
  /**
   * The readable source name per diagnostic.
   *
   * Precomputed, because a source is a property of the diagnostic rather than of the reading:
   * derived per reading it was the same dozen values parsed tens of thousands of times.
   */
  sources: Record<string, string>;
}

/**
 * Turns one raw `StatusData` reading into a displayable row.
 *
 * Shared because the diagnostic name, its unit and its source all come from separate queries
 * that arrive later than the reading itself — every page showing readings has to combine them
 * the same way, or the same reading appears differently on two pages.
 *
 * @param statusData - The reading.
 * @param lookups - See {@link LiveDataLookups}.
 */
export function toLiveDataRow(statusData: StatusData, lookups: LiveDataLookups): LiveDataRow {
  const diagnosticId = statusData.diagnostic?.id ?? "";
  const diagnostic = diagnosticId ? lookups.diagnostics[diagnosticId] : undefined;
  const unit = diagnosticId ? lookups.units[diagnosticId] : undefined;

  return {
    id: statusData.id,
    diagnosticId,
    diagnosticName: diagnostic?.name ?? diagnosticId,
    value: statusData.data,
    unitOfMeasure: unit?.name ?? "",
    unitFactor: unit?.factorFromBase,
    unitOffset: unit?.offsetFromBase,
    dateTime: statusData.dateTime,
    source: (diagnosticId ? lookups.sources[diagnosticId] : "") ?? "",
  };
}
